import BaseButton from '@/components/base/BaseButton';
import BaseLoading from '@/components/base/BaseLoading';
import BaseModal from '@/components/base/BaseModal';
import DetailTable from '@/components/composite/DetailTable';
import { useIntl } from '@/i18n';
import type { IamSessionResp } from '@/types/iam-admin';
import {
  IdcardOutlined,
  LockOutlined,
  LogoutOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Alert, Flex, Typography } from 'antd';

interface CurrentLoginModalProps {
  open: boolean;
  name: string;
  session?: IamSessionResp;
  failed: boolean;
  detailLoading: boolean;
  onClose: () => void;
  onDetail: () => void;
  onPassword: () => void;
  onLogout: () => void;
  onRetry: () => void;
}

/** 当前登录仅组织界面；正式会话、重试和帐号操作由原控制层注入。 */
export default function CurrentLoginModal({
  open,
  name,
  session,
  failed,
  detailLoading,
  onClose: close,
  onDetail,
  onPassword,
  onLogout,
  onRetry,
}: CurrentLoginModalProps) {
  const intl = useIntl();
  const t = (id: string, values?: Record<string, unknown>) =>
    intl.formatMessage({ id }, values);
  const scopeKey =
    session && ['all', 'dept', 'self'].includes(session.dataScope)
      ? `scope.${session.dataScope}`
      : undefined;
  return (
    <BaseModal
      open={open}
      width={760}
      title={t('layout.session.title', { name: session?.accountName ?? name })}
      icon={<UserOutlined aria-hidden />}
      onClose={close}
      footer={
        <Flex wrap gap={8} justify="flex-end">
          <BaseButton
            icon={<IdcardOutlined aria-hidden />}
            autoInsertSpace={false}
            disabled={!session}
            loading={detailLoading}
            onClick={onDetail}
          >
            {t('layout.session.detail')}
          </BaseButton>
          <BaseButton
            icon={<LockOutlined aria-hidden />}
            autoInsertSpace={false}
            onClick={onPassword}
          >
            {t('layout.changePassword')}
          </BaseButton>
          <BaseButton
            icon={<LogoutOutlined aria-hidden />}
            danger
            type="primary"
            autoInsertSpace={false}
            onClick={onLogout}
          >
            {t('layout.logout')}
          </BaseButton>
        </Flex>
      }
    >
      {failed ? (
        <Alert
          type="error"
          showIcon
          title={t('http.requestFailed')}
          action={
            <BaseButton autoInsertSpace={false} onClick={onRetry}>
              {t('common.retry')}
            </BaseButton>
          }
        />
      ) : !session ? (
        <div className="flex min-h-60 items-center justify-center">
          <BaseLoading label={t('common.loading')} />
        </div>
      ) : (
        <DetailTable
          items={[
            {
              key: 'name',
              label: t('adminAcct.field.accountName'),
              children: (
                <>
                  <Typography.Text strong>
                    {session.accountName ?? '—'}
                  </Typography.Text>
                  {session.nameSource !== null && (
                    <div className="text-xs text-secondary mt-1">
                      {session.nameSource}
                    </div>
                  )}
                </>
              ),
            },
            {
              key: 'identity',
              label: t('layout.session.identity'),
              children: session.identityTitle ?? '—',
            },
            {
              key: 'email',
              label: t('adminAcct.field.loginEmail'),
              children: session.loginEmail ?? '—',
            },
            {
              key: 'pages',
              label: t('layout.session.pages'),
              children: (
                <Typography.Text strong>
                  {session.superAdmin
                    ? t('adminAcct.detail.allPages')
                    : t('adminAcct.detail.pageCount', {
                        count: session.pageCount,
                      })}
                </Typography.Text>
              ),
            },
            {
              key: 'scope',
              label: t('layout.session.scope'),
              children: scopeKey ? t(scopeKey) : session.dataScope,
            },
          ]}
        />
      )}
    </BaseModal>
  );
}
